'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Toaster, toast as sonnerToast } from 'sonner';
import { I, Icon } from '../../system/icons';
import { TONE } from '../../system/tone';
import styles from './Toast.module.css';
// The badge supplies the circle, so error and info draw only the bare mark —
// lucide's versions of both bring a circle of their own.
const exclamation = (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", width: "100%", height: "100%", "aria-hidden": true, children: [_jsx("path", { d: "M12 6v8" }), _jsx("circle", { cx: "12", cy: "18", r: "1.25", fill: "currentColor", stroke: "none" })] }));
const infoMark = (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", width: "100%", height: "100%", "aria-hidden": true, children: [_jsx("circle", { cx: "12", cy: "6", r: "1.25", fill: "currentColor", stroke: "none" }), _jsx("path", { d: "M12 10v8" })] }));
function badge(tone, glyph) {
    return (_jsx("span", { className: styles.badge, style: { background: TONE[tone].bg, color: TONE[tone].ink }, children: _jsx(Icon, { size: "sm", children: glyph }) }));
}
/**
 * Mount once in the app shell. Configures the Sonner Toaster with Lean tokens and a11y roles.
 */
export function ToastProvider({ position = 'bottom-right', duration = 5000, offset, mobileOffset, visibleToasts = 5, dir = 'auto', }) {
    return (_jsx(Toaster, { position: position, duration: duration, offset: offset, mobileOffset: mobileOffset, visibleToasts: visibleToasts, dir: dir, 
        // Sonner sets `--width` inline and its own z-index on the toaster, so
        // both can only be overridden here, not from the stylesheet.
        style: { '--width': '28rem', zIndex: 'var(--z-toast)' }, icons: {
            success: badge('emerald', I.check),
            error: badge('burgundy', exclamation),
            warning: badge('yellow', I.alert),
            info: badge('navy', infoMark),
        }, toastOptions: {
            // Sonner's built-in visuals use attribute selectors that outrank these
            // module classes; unstyled keeps only its positioning and motion.
            unstyled: true,
            classNames: {
                toast: styles.toast,
                title: styles.toastTitle,
                description: styles.toastDescription,
                icon: styles.icon,
                content: styles.content,
                actionButton: styles.actionButton,
                cancelButton: styles.cancelButton,
                closeButton: styles.closeButton,
            },
        } }));
}
// Sonner shows the spinner before it calls a factory, outside any try, so a
// factory that throws synchronously would strand it. The executor turns that
// throw into a rejection, which ends the spinner as the error toast.
function promiseToast(promise, messages) {
    const running = new Promise((resolve) => {
        resolve(typeof promise === 'function' ? promise() : promise);
    });
    sonnerToast.promise(running, messages);
}
export const toast = {
    success: sonnerToast.success,
    error: sonnerToast.error,
    warning: sonnerToast.warning,
    info: sonnerToast.info,
    message: (title, data) => sonnerToast(title, data),
    dismiss: sonnerToast.dismiss,
    promise: promiseToast,
};
//# sourceMappingURL=Toast.js.map